import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../requestOptions';

export interface PostingTemplateHistoryEntry {
  id: number;
  posting_template_id: number;
  user_name: string | null;
  timestamp: string;
  template_changes: Record<string, unknown>[];
  mapping_changes: Record<string, unknown>[];
  change_descriptions: string[];
}

export interface PostingTemplateHistoryPagination {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

export interface PostingTemplateHistoryData {
  data: PostingTemplateHistoryEntry[];
  pagination: PostingTemplateHistoryPagination;
}

type PostingTemplateHistoryApiEnvelope = {
  data?: PostingTemplateHistoryEntry[];
  pagination?: PostingTemplateHistoryPagination;
};

export async function getPostingTemplateHistory(
  scope: RequestScope,
  args: {
    subscriptionId: number;
    postingTemplateId: number;
    page?: number;
    limit?: number;
  },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<PostingTemplateHistoryData>> {
  try {
    if (!args.subscriptionId || args.subscriptionId < 1) {
      throw new Error('Valid subscriptionId is required');
    }
    if (!args.postingTemplateId || args.postingTemplateId < 1) {
      throw new Error('Valid postingTemplateId is required');
    }

    const params: Record<string, string> = {};
    if (args.page && args.page > 0) {
      params.page = args.page.toString();
    }
    if (args.limit && args.limit > 0) {
      params.limit = args.limit.toString();
    }

    const res = await scopedChannelApi(scope).get<PostingTemplateHistoryApiEnvelope>(
      `astur/integrations/posting-templates/${args.postingTemplateId}/history`,
      {
        ...options,
        params,
        headers: {
          'x-astur-subscription-id': args.subscriptionId.toString(),
        },
      }
    );

    const data: PostingTemplateHistoryEntry[] = Array.isArray(res.data?.data) ? res.data!.data : [];
    const pagination: PostingTemplateHistoryPagination = res.data?.pagination ?? {
      total: 0,
      per_page: 20,
      current_page: 1,
      last_page: 1,
    };

    return { data: { data, pagination }, status: res.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
}
